import { Reset } from '@sas/react-svg';
import { createElement, useRef, useState } from 'react';
import { createUseTextLocalizer } from '../../../locale/createUseTextLocalizer';
import { useIconA11yInfo } from '../../Icon/useIconA11yInfo';
import { Button, ButtonVariant } from '../../Button';
import { BaseInput } from '../../Input/__internal__/BaseInput';
import { Flow } from '../../Layouts/Flow';
import { ShortcutsInputProps } from '.';
import { getKey, getKeyLabel } from '..';
import bundle from '../NovaComponents-KeyboardShortcuts-gui-icu.properties';
import cn from './ShortcutsInput.css.json';
import './ShortcutsInput.css';

const useTextLocalizer = createUseTextLocalizer(bundle);

export const ShortcutsInput = (props: ShortcutsInputProps) => {
  const localize = useTextLocalizer();

  const getIconA11yInfo = useIconA11yInfo();
  const resetIconA11yInfo = getIconA11yInfo({ icon: Reset });
  const clearButtonTooltip = localize('clear.button.tooltip.label');

  const [tempValue, setTempValue] = useState<string | undefined>();

  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleKeyDown = (e: KeyboardEvent) => {
    if (props.readOnly || e.key === 'Tab') return; // need the ability to focus out

    e.stopPropagation();
    e.preventDefault();
    setTempValue(getKey(e, props.platform));
  };
  const handleKeyUp = () => {
    if (tempValue === undefined) return;
    props.onChange?.(tempValue);
    setTempValue(undefined);
  };

  return (
    <Flow spacing="xxs" className={props.className} style={props.style}>
      <BaseInput
        className={cn.input}
        value={getKeyLabel(tempValue ?? props.value, localize, props.platform)}
        placeholder={localize('pressKeys.placeholder.label')}
        ref={(element) => {
          if (element) {
            element.addEventListener('keydown', handleKeyDown);
            element.addEventListener('keyup', handleKeyUp);
          } else {
            inputRef.current?.removeEventListener('keydown', handleKeyDown);
            inputRef.current?.removeEventListener('keyup', handleKeyUp);
          }
          inputRef.current = element;
        }}
        showClearButton
        clearButtonTooltip={clearButtonTooltip}
        clearButtonAriaLabel={`${props.ariaLabel} ${clearButtonTooltip}`}
        onChange={() => props.onChange?.('')}
        onBlur={() => setTempValue(undefined)}
        editable={!props.readOnly}
        status={props.showError ? 'alert' : undefined}
        message={localize('input.required.msg')}
        ariaLabel={props.ariaLabel}
      />
      {props.readOnly ? null : (
        <Button
          className={cn.button}
          icon={{ icon: Reset }}
          variant={ButtonVariant.Icon}
          disabled={props.defaultValue === '' || props.defaultValue === props.value}
          onPress={() => props.onChange?.(props.defaultValue)}
          ariaLabel={`${props.ariaLabel} ${resetIconA11yInfo.ariaLabel}`}
        />
      )}
    </Flow>
  );
};
